import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';
import { ModeratorViewModel } from '../Model/moderatorViewModel';
import { RequestService } from '../services/requests.services';
import url from 'src/settings.json';
import { Setting } from '../../common/settings';
import { UserInformation } from '../Model/userInformation';
import { ModifiedRequest } from '../Model/modifiedRequest';
import { ModifiedItem } from '../../common/modifiedItem';
import { HttpClient, HttpParams } from '@angular/common/http';
import { OrganisationService } from '../../organisation/organisation/services/organisation.service';
import { RequestReviewerViewModel } from '../Model/requestReviewerViewModel';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-request-moderators',
  templateUrl: './request-moderators.component.html',
  styleUrls: ['./request-moderators.component.css']
})
export class RequestModeratorsComponent implements OnInit, OnDestroy {
  @Input() requestId: number | undefined;
  @Input() canModify = false;
  @Input() project!: string;
  moderatorsList!: ModeratorViewModel[];
  isListloading = false;
  displayedColumns: string[] = ['name'];

  errorMessage = "";
  isEditable = false;
  displayedColumnsToEdit: string[] = ['name', 'action'];
  isModeratorLoading = false;
  addModeratorControl = new FormControl();
  moderatorsfilteredOptions!: Observable<any>;
  setting: Setting = url;
  currentOrganisation: any;
  organisationSubscriber?: Subscription;
  currentUser!: UserInformation;
  newModeratorsList: Array<ModeratorViewModel> = [];
  moderatorsOptions: Array<UserInformation> = [];
  reviewerIds: Array<string> = [];
  isModified = false;
  isRequestor = false;
  isModeratorAddedSuccessfully = false;

  reviewRequest: ModifiedRequest = {
    requestId: 0,
    moderators: [],
    tasks: [],
    reviewers: [],
    modifiedItem: ModifiedItem.Moderator
  }
 
  constructor(private readonly requestService: RequestService, private readonly http: HttpClient, private readonly organisationService: OrganisationService) {    
    this.organisationSubscriber = this.organisationService.organisationEmitter.subscribe({
      next: (value: string) => {
        if (value === '') { this.currentOrganisation = this.organisationService.getLocalStorageItem("CurrentOrganisation") }
        else { this.currentOrganisation = value; }
      }
    });
    this.GetCurrentUser();
  }

  ngOnInit(): void {
    console.log("RequestId for Moderators is: " + this.requestId);
    this.getRequestModerators(Number(this.requestId));

    this.moderatorsfilteredOptions = this.addModeratorControl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      filter((value) => value !== ""),
      switchMap(val => {
        return this.filterModerators(val)
      }))
  }

  ngOnDestroy(): void {
    this.organisationSubscriber?.unsubscribe();
  }  
  
  getRequestModerators(id: number) {
    this.isListloading = true;
    this.requestService.getrequestModerators(id).subscribe({
      next: (response: ModeratorViewModel[]) => {
        this.moderatorsList = response;
      },
      error: (error: any) => {
        console.log(error)
      },
      complete: () => { this.isListloading = false; }
    });
  }

  onEditClick() {
    this.isEditable = true;

    this.moderatorsList.forEach((moderator) => {
      this.newModeratorsList.push(moderator);
    });

    this.getRequestReviewers(Number(this.requestId));      
  }

  getRequestReviewers(id: number) {
    this.requestService.getrequestReviewers(id).subscribe({
      next: (response: RequestReviewerViewModel[]) => {
        response.map((r) => {
          this.reviewerIds.push(r.reviewerId);
        });
      },
      error: (error: any) => {
        console.log(error)
      },
      complete: () => { this.isListloading = false; }
    });
  }

  GetCurrentUser() {
    this.requestService.getCurrentUser().subscribe({
      next: (user: UserInformation) => {
        this.currentUser = user;
      },
      error: (err: any) => {        
        this.errorMessage = err.error;
     },
      complete: () => { }
    });
  }

  filterModerators(val: string): Observable<UserInformation[]> {
    this.errorMessage = "";
    
    return this.getModerators(val)
      .pipe(
        map(response => response.filter((option: UserInformation) => {
          this.isModeratorLoading = false;
          return option.displayName;
        }))
      )
  }

  getModerators(val: string) {
    this.isModeratorLoading = true;
    const url = `${this.setting.url}/${this.currentOrganisation}`;
    const searchTerm = new HttpParams().set('searchTerm', val);
    return this.moderatorsOptions.length ?
      of(this.moderatorsOptions) :
      this.http.get<UserInformation[]>(`${url}/api/user/`, { params: searchTerm }).pipe(tap(data => this.moderatorsOptions = data))
  }  

  onAddModeratorSelected(event: MatAutocompleteSelectedEvent) {
    var user = this.moderatorsOptions.find(x => x.adsId === event.option.value) as UserInformation;

    if (user.isContainer) {
      this.requestService.getTeamMembers(user.adsId, this.project).subscribe({
        next: (data) => {
          for (let Data of data) {
            this.pushToModeratorsArray({ name: Data.displayName, moderatorId: Data.adsId });
          }
        }
      })
    }
    else {
      this.pushToModeratorsArray({ name: user.displayName, moderatorId: user.adsId });
    }

    this.moderatorsOptions = [];
    this.addModeratorControl.setValue("");
    event.option.value = '';
  }

  pushToModeratorsArray(moderator: ModeratorViewModel) {
    var currentUserAdsId = this.currentUser.adsId;

    if (currentUserAdsId === moderator.moderatorId) {
      this.errorMessage = 'The selected user is requestor of this request.';
      return;
    }

    if (this.reviewerIds.indexOf(moderator.moderatorId) >= 0) {
      this.errorMessage = 'The selected user is reviewer of this request.';
      return;
    }

    if (this.newModeratorsList.find(m => m.moderatorId === moderator.moderatorId) === undefined) {
      this.newModeratorsList.push(moderator);
      this.isModified = true;
    } else {
      this.errorMessage = "This moderator is already selected!";
    }
  }

  removeModerator(moderator: ModeratorViewModel) {
    const index = this.newModeratorsList.indexOf(moderator);
    this.newModeratorsList.splice(index, 1);
    this.isModified = true;
  }

  onSaveClick() {
    this.isModeratorLoading = true;
    this.reviewRequest.requestId = Number(this.requestId);
    this.reviewRequest.moderators = this.newModeratorsList;

    this.requestService.modifyModeratorsOfRequest(this.reviewRequest).subscribe({
      next: (response: ModifiedRequest) => {
        this.moderatorsList = [];
        this.moderatorsList = response.moderators;        
        this.isModeratorAddedSuccessfully = true;
        this.newModeratorsList = [];
        this.isEditable = false;
        this.isModified = false;
        this.isModeratorLoading = false;
      },
      error: (err: any) => { this.errorMessage = err.error; this.isModeratorLoading = false; },
      complete: () => {
        this.isModeratorLoading = false;
        this.moderatorsOptions = [];
        this.addModeratorControl.setValue("");
        this.errorMessage = "";
      }
    });
  }

  onCancelClick() {
    this.isEditable = false;
    this.isModified = false;
    this.newModeratorsList = [];
    this.moderatorsOptions = [];
    this.addModeratorControl.setValue("");
    this.errorMessage = "";
  }
}
